import React, { createContext, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { NUMBER } from '@constants/common/number';
import { LAST_DAY_OF_YEAR, TXN_TYPES } from '@constants/enrollmentList/enrollmentList.constant';
import { zodResolver } from '@hookform/resolvers/zod';


import { addOrChangeTxnFormSchema } from '../common/addOrChangeTxnFormConfig';
import { getMemberOrder } from '../common/enrollmentUtils';
import { IAddOrChangeTxnContextProps, IEnrollment } from '../common/types';
import { EnrollmentContext } from './MemberMyEnrollmentProvider';

export const AddOrChangeTxnFormContext = createContext<IAddOrChangeTxnContextProps>({
    watch: () => {},
    formState: {
        errors: {},
        isDirty: false,
        isValid: false,
        dirtyFields: {},
        touchedFields: {},
        isSubmitted: false,
        isSubmitting: false,
        submitCount: 0,
    },
    setError: () => {},
    setValue: () => {},
    clearErrors: () => {},
});
export const AddOrChangeTxnProvider = ({ children }) => {
  
    const { enrollmentPremiumFormContext, isEdit } = useContext(EnrollmentContext);
    const { watch } = enrollmentPremiumFormContext;
    const values = watch();

    const { selectedEnrollment } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);

    const getDefaultValues = useMemo(() => {
        let enrolleeDataDtoList = values?.enrolleeDataDtoList;

        if (!isEdit) {
            enrolleeDataDtoList = values?.enrolleeDataDtoList?.map(enrollee => ({
                ...enrollee,
                enrolleeEffectiveEndDateUI: enrollee.enrolleeEffectiveEndDateUI.replace(
                    enrollee.enrolleeEffectiveEndDateUI.substring(0, NUMBER.FIVE),
                    LAST_DAY_OF_YEAR,
                ),
                txnType: true,
                txnReason: '',
                order: getMemberOrder(enrollee.relationshipToSubscriber),
            }));
            enrolleeDataDtoList = enrolleeDataDtoList.sort((a, b) => a.order - b.order);
        }
        return {
            ...values,
            enrolleeDataDtoList: enrolleeDataDtoList,
            addOrChangeComment: '',
            addOrChangeSendIn834: true,
            sendTermNoticeVM: undefined,
            isTermReasonNonPayment: false,
            enrollmentLastPremPaidThroughDateUI: '',
            endDateUnchanged: false,
            enrollmentLevelTxnType: isEdit ? TXN_TYPES.Change : TXN_TYPES.Add,
            aptcAmount: selectedEnrollment?.aptcAmount?.toString() || null,
        };
    }, [selectedEnrollment, isEdit]);

    const methods = useForm<IEnrollment>({
        defaultValues: getDefaultValues,
        resolver: zodResolver(addOrChangeTxnFormSchema(isEdit)),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const value = {
        watch: methods.watch,
        formState: methods.formState,
        setValue: methods.setValue,
        clearErrors: methods.clearErrors,
        setError: methods.setError,
        handleSubmit: methods.handleSubmit,
    };
    return <AddOrChangeTxnFormContext.Provider value={value}>{children}</AddOrChangeTxnFormContext.Provider>;
};
